import { useCallback, useMemo, useState } from 'react';
import CheckableSelect from 'components/CheckableSelect/CheckableSelect';
import { useReportingExcludeDates } from 'hooks/useFeatureFlag';
import { capitalize, difference, intersection } from 'lodash';
import {
  EReportingTimeType,
  EWeekdays,
  type TReportTemplate,
  type TTimeSetting,
  type TWeekDaySettings,
} from '@timeedit/types';
import { Icon, TEInput } from '@timeedit/ui-components';
import { Button, Space } from '@timeedit/ui-components/antd';
import { ExcludedTimes } from '../components/ExcludedTimes';
import TimeRangePicker from '../components/TimeRangePicker';

type Props = {
  reportTemplate: TReportTemplate;
  onChange: (weekdaySettings: TWeekDaySettings) => void;
};

const WeekdaySettings = ({ reportTemplate, onChange }: Props) => {
  const {
    availableType,
    weekdaySettings,
    availableStartTime,
    availableEndTime,
    availableHour,
    defaultExcludedTimes,
  } = reportTemplate;
  const [openAdvanced, setOpenAdvanced] = useState(false);

  const enableExcudeDates = useReportingExcludeDates();

  const selectedWeekdays = useMemo(() => {
    if (!weekdaySettings) return [];
    return Object.keys(weekdaySettings);
  }, [weekdaySettings]);

  const handleSelectWeekdays = useCallback(
    (value: string[]) => {
      const oldValues = intersection(selectedWeekdays, value);
      const newValues = difference(value, selectedWeekdays);

      const updatedSettings: TWeekDaySettings = oldValues.reduce(
        (acc, weekday) => {
          acc[weekday] = weekdaySettings[weekday];
          return acc;
        },
        {}
      );

      newValues.forEach((weekday) => {
        switch (availableType) {
          case EReportingTimeType.FIXED_HOUR:
            updatedSettings[weekday] = {
              availableTime: availableHour,
            };
            break;
          case EReportingTimeType.TIME_RANGE:
            updatedSettings[weekday] = {
              startTime: availableStartTime,
              endTime: availableEndTime,
              excludedTimes: defaultExcludedTimes,
            };
            break;
          default:
            updatedSettings[weekday] = {};
            break;
        }
      });

      onChange(updatedSettings);
    },
    [
      weekdaySettings,
      availableEndTime,
      availableStartTime,
      availableHour,
      onChange,
      selectedWeekdays,
    ]
  );

  const handleTimeSettingChange = useCallback(
    (weekday: EWeekdays, timeSetting: TTimeSetting) => {
      onChange({
        ...weekdaySettings,
        [weekday]: timeSetting,
      });
    },
    [onChange, weekdaySettings]
  );

  return (
    <Space direction="vertical">
      <CheckableSelect
        options={weekdayOptions}
        label="Select weekday(s)"
        tooltip="Can customize available time for each weekday below"
        value={selectedWeekdays}
        onChange={handleSelectWeekdays}
        highlightValue={[EWeekdays.SUN]}
      />
      {(availableType === EReportingTimeType.FIXED_HOUR ||
        availableType === EReportingTimeType.TIME_RANGE) && (
        <>
          <Button
            type="link"
            size="small"
            icon={
              openAdvanced ? (
                <Icon id="DownOutlined" alt="Down" size={12} />
              ) : (
                <Icon id="RightOutlined" alt="Right" size={12} />
              )
            }
            iconPosition="end"
            onClick={() => setOpenAdvanced(!openAdvanced)}
          >
            Customized
          </Button>
          {openAdvanced && (
            <>
              {Object.values(EWeekdays).map((weekday) => {
                const isDisabled = !selectedWeekdays.includes(weekday);
                const tooltip = isDisabled
                  ? 'Need to enable this weekday to customize available time'
                  : `Input available time for ${weekdayNames[weekday]}`;

                return (
                  <>
                    {availableType === EReportingTimeType.TIME_RANGE && (
                      <>
                        <TimeRangePicker
                          key={weekday}
                          label={weekdayNames[weekday]}
                          tooltip={tooltip}
                          onChange={(timeSetting) =>
                            handleTimeSettingChange(weekday, timeSetting)
                          }
                          value={weekdaySettings?.[weekday]}
                          disabled={isDisabled}
                        />
                        {enableExcudeDates && (
                          <ExcludedTimes
                            excludedTimes={
                              weekdaySettings?.[weekday]?.excludedTimes
                            }
                            onChange={(excludedTimes) => {
                              const currentSetting =
                                weekdaySettings?.[weekday] || {};
                              handleTimeSettingChange(weekday, {
                                ...currentSetting,
                                excludedTimes,
                              });
                            }}
                            disabled={isDisabled}
                          />
                        )}
                      </>
                    )}
                    {availableType === EReportingTimeType.FIXED_HOUR && (
                      <TEInput.InputNumber
                        key={weekday}
                        label={weekdayNames[weekday]}
                        tooltip={tooltip}
                        onChange={(val: number) =>
                          handleTimeSettingChange(weekday, {
                            availableTime: val,
                          })
                        }
                        value={weekdaySettings?.[weekday]?.availableTime}
                        disabled={isDisabled}
                      />
                    )}
                  </>
                );
              })}
            </>
          )}
        </>
      )}
    </Space>
  );
};

export default WeekdaySettings;

const weekdayOptions = Object.values(EWeekdays).map((weekday) => ({
  value: weekday,
  label: capitalize(weekday),
}));

const weekdayNames: Record<EWeekdays, string> = {
  [EWeekdays.MON]: 'Monday',
  [EWeekdays.TUE]: 'Tuesday',
  [EWeekdays.WED]: 'Wednesday',
  [EWeekdays.THU]: 'Thursday',
  [EWeekdays.FRI]: 'Friday',
  [EWeekdays.SAT]: 'Saturday',
  [EWeekdays.SUN]: 'Sunday',
};
